import { useState } from "react";
import { ButtonLink } from "../../components/ui/Button";
import SectionHeading from "../../components/ui/SectionHeading";
import { selector, sizes, trades } from "../../data/home";
import { routes } from "../../data/site";
import { cx } from "../../lib/cx";
import { localHref } from "../../lib/links";

function Chips({ id, label, options, value, onChange }) {
  return (
    <>
      <div
        id={id}
        className="tag mt-6 mb-3 text-[12.5px] tracking-[0.12em] text-muted"
      >
        {label}
      </div>
      <div role="group" aria-labelledby={id} className="flex flex-wrap gap-2.5">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={value === option.id}
            onClick={() => onChange(option.id)}
            className={cx(
              "rounded-lg border-2 px-5 py-3 text-base font-bold transition-colors",
              value === option.id
                ? "border-yellow bg-yellow text-slate"
                : "border-line-strong bg-white text-ink hover:border-blue",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </>
  );
}

/** The case study beside the headaches: a company like yours, in their words. */
function Story({ title, body, href, quote, name, role, results }) {
  return (
    <div className="flex flex-col gap-4 rounded-brand border border-line bg-white p-7 md:p-9">
      <span className="tag text-[11px] text-blue-deep">
        What we did for a company like yours
      </span>
      <h3 className="font-display text-[26px] text-slate">{title}</h3>
      <p className="text-muted">{body}</p>
      {quote && (
        <blockquote className="border-l-4 border-yellow py-1.5 pl-4.5 leading-[1.7] text-ink">
          <p>“{quote}”</p>
          <footer className="mt-3 before:mb-2 before:block before:h-[3px] before:w-[22px] before:bg-yellow">
            <strong className="tag block text-[15px] font-semibold tracking-[0.05em] text-ink">
              {name}
            </strong>
            <span className="text-[13px] text-muted">{role}</span>
          </footer>
        </blockquote>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-3 pt-2">
        <ButtonLink href={href}>Read the story</ButtonLink>
        {results && (
          <a
            href={`${localHref(routes.caseStudies)}#${results}`}
            className="font-bold text-blue-deep hover:underline"
          >
            See more companies like yours →
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * "What do you run?": pick a trade (and optionally a size) to see the
 * headaches we usually find and a case study from a similar company.
 */
export default function FitSelector() {
  const [tradeId, setTrade] = useState(null);
  const [sizeId, setSize] = useState(null);
  const trade = trades.find(({ id }) => id === tradeId);
  const size = sizes.find(({ id }) => id === sizeId);

  return (
    <section id="fit" className="bg-surface px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading eyebrow={selector.eyebrow} title={selector.title}>
          <p className="max-w-[640px]">{selector.text}</p>
        </SectionHeading>
        <div className="mt-6">
          <Chips
            id="trade-label"
            label={selector.tradeLabel}
            options={trades}
            value={tradeId}
            onChange={setTrade}
          />
          <Chips
            id="size-label"
            label={selector.sizeLabel}
            options={sizes}
            value={sizeId}
            onChange={setSize}
          />
        </div>

        <div aria-live="polite">
          {trade && (
            <div
              key={trade.id}
              className={cx(
                "mt-11 grid animate-rise grid-cols-1 gap-7",
                trade.story && "lg:grid-cols-[1.1fr_0.9fr]",
              )}
            >
              <div className="rounded-brand bg-slate px-7 pt-8 pb-7 text-white md:px-9">
                <span className="tag text-[11px] text-yellow">
                  Sound familiar?
                </span>
                <h3 className="font-display mt-2.5 mb-2 text-[30px] uppercase">
                  {trade.label}
                </h3>
                {size && (
                  <p className="mb-4 text-[15.5px] text-blue-soft">
                    {size.note}
                  </p>
                )}
                <ul>
                  {trade.pains.map((pain) => (
                    <li
                      key={pain}
                      className="relative border-t border-white/12 py-3 pl-8 text-[16.5px]"
                    >
                      <span
                        aria-hidden="true"
                        className="absolute top-3 left-1 font-bold text-yellow"
                      >
                        ✕
                      </span>
                      {pain}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 border-t-2 border-yellow pt-4 text-white/90">
                  {trade.relief}
                </p>
                {!trade.story && (
                  <div className="mt-6">
                    <ButtonLink />
                  </div>
                )}
              </div>

              {trade.story && (
                <Story {...trade.story} results={trade.results} />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
